import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

const Contact = ({ currentUser, onBack }) => {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [name, setName] = useState(currentUser?.name || '')
  const [email, setEmail] = useState(currentUser?.email || '')
  const [deviceType, setDeviceType] = useState('none')
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    if (deviceType === 'none') {
      setError(t('contact_device_error'))
      setIsSubmitting(false)
      return
    }

    try {
      const response = await fetch('/api/v1/inquiries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ inquiry: { title, email, name, device_type: deviceType, message } })
      })

      if (response.ok) {
        setSuccess(true)
        setTitle('')
        setDeviceType('none')
        setMessage('')
      } else {
        const data = await response.json()
        setError(data.errors ? data.errors.join(', ') : 'Something went wrong')
      }
    } catch (err) {
      setError('Failed to send inquiry')
      console.error(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (success) {
    return (
      <div className="max-w-md mx-auto p-6 bg-[#1c1c1e] rounded-xl border border-gray-800 shadow-lg mt-8 text-center">
        <h2 className="text-xl font-bold text-white mb-4">{t('contact_success_title')}</h2>
        <p className="text-gray-400 mb-6">{t('contact_success_message')}</p>
        <button
          onClick={onBack}
          className="bg-[#2c2c2e] text-white font-bold py-2 px-4 rounded-lg hover:bg-[#3a3a3c] transition-colors"
        >
          {t('back_to_dashboard')}
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto p-4">
      <div className="flex items-center mb-6">
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-white transition-colors mr-4"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <h1 className="text-2xl font-bold text-white">{t('contact_title')}</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-bold text-gray-400 mb-1">{t('contact_subject_label')}</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-gray-400 mb-1">{t('contact_name_label')}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-gray-400 mb-1">{t('contact_email_label')}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
          />
          {email.endsWith('@example.com') && (
            <p className="text-yellow-500 text-sm mt-1">{t('contact_email_warning_example_com')}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-bold text-gray-400 mb-1">{t('contact_device_label')}</label>
          <select
            value={deviceType}
            onChange={(e) => setDeviceType(e.target.value)}
            className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none"
          >
            <option value="none">{t('device_types.none')}</option>
            <option value="iphone">{t('device_types.iphone')}</option>
            <option value="android">{t('device_types.android')}</option>
            <option value="mac">{t('device_types.mac')}</option>
            <option value="windows">{t('device_types.windows')}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-bold text-gray-400 mb-1">{t('contact_message_label')}</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows="5"
            className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
          ></textarea>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {isSubmitting ? t('processing') : t('contact_submit')}
        </button>
      </form>
    </div>
  )
}

export default Contact
